/**
 * Validation.
 *
 * Two layers, and only two:
 *
 *   1. DEVICE limits (limits.ts) — the firmware returns ERR outside these.
 *      Not adjustable by anyone.
 *   2. AVOIDED BANDS — frequencies this profile has asked to skip. Entirely
 *      the person's own choice; empty by default.
 */

import { LIMITS, USEFUL_CARRIER_RANGE } from './limits';
import type { Band, CycleSettings, Program } from './types';

export type Severity = 'error' | 'warning';

export type Issue = {
  severity: Severity;
  code: string;
  message: string;
  /** 0-based index into program.cycles when the issue belongs to one step. */
  cycleIndex?: number;
  field?: keyof CycleSettings | 'name' | 'cycles' | 'session';
};

/* ------------------------------------------------------------------ *
 * Interval helpers
 * ------------------------------------------------------------------ */

/**
 * Do two closed intervals overlap?
 *
 * This is what makes sweep checking correct. A sweep from 8 to 40 Hz never
 * *names* 10 Hz, but the device steps 1 Hz at a time and passes straight
 * through it. Comparing endpoints misses that; comparing intervals does not.
 */
export function intervalsOverlap(aLo: number, aHi: number, bLo: number, bHi: number): boolean {
  return aLo <= bHi && bLo <= aHi;
}

export function bandsHittingPoint(hz: number, bands: Band[]): Band[] {
  return bands.filter((b) => hz >= b.lo && hz <= b.hi);
}

export function bandsHittingSweep(lo: number, hi: number, bands: Band[]): Band[] {
  return bands.filter((b) => intervalsOverlap(lo, hi, b.lo, b.hi));
}

/* ------------------------------------------------------------------ *
 * Duration
 * ------------------------------------------------------------------ */

/**
 * Total program length in minutes: every step's ON time, plus the rest after
 * each step except the last — the device stops rather than resting at the end.
 */
export function totalSessionMinutes(cycles: CycleSettings[]): number {
  return cycles.reduce((sum, c, i) => {
    const rest = i < cycles.length - 1 ? c.offMin : 0;
    return sum + c.onMin + rest;
  }, 0);
}

/** Minutes the coil is actually energised. */
export function treatmentMinutes(cycles: CycleSettings[]): number {
  return cycles.reduce((sum, c) => sum + c.onMin, 0);
}

export function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/* ------------------------------------------------------------------ *
 * Device limits
 * ------------------------------------------------------------------ */

function inRange(v: number, r: { min: number; max: number }): boolean {
  return Number.isFinite(v) && v >= r.min && v <= r.max;
}

export function validateCycle(c: CycleSettings, index: number): Issue[] {
  const issues: Issue[] = [];
  const at = (
    field: Issue['field'],
    code: string,
    message: string,
    severity: Severity = 'error',
  ) => issues.push({ severity, code, message, cycleIndex: index, field });

  if (!Number.isInteger(c.onMin) || !inRange(c.onMin, LIMITS.onMin)) {
    at('onMin', 'ON_RANGE', `Duration must be ${LIMITS.onMin.min}–${LIMITS.onMin.max} minutes.`);
  }
  if (!Number.isInteger(c.offMin) || !inRange(c.offMin, LIMITS.offMin)) {
    at('offMin', 'OFF_RANGE', `Rest must be ${LIMITS.offMin.min}–${LIMITS.offMin.max} minutes.`);
  }
  if (!inRange(c.carrierHz, LIMITS.carrierHz)) {
    at('carrierHz', 'CARRIER_RANGE', `Carrier must be ${LIMITS.carrierHz.min}–${LIMITS.carrierHz.max} Hz.`);
  }
  if (!Number.isInteger(c.intensity) || !inRange(c.intensity, LIMITS.intensity)) {
    at('intensity', 'INTENSITY_RANGE', 'Intensity must be 0–100%.');
  }

  if (c.sweepEnabled) {
    if (!inRange(c.sweepLo, LIMITS.gateHz)) {
      at('sweepLo', 'SWEEP_LO_RANGE', `Sweep start must be ${LIMITS.gateHz.min}–${LIMITS.gateHz.max} Hz.`);
    }
    if (!inRange(c.sweepHi, LIMITS.gateHz)) {
      at('sweepHi', 'SWEEP_HI_RANGE', `Sweep end must be ${LIMITS.gateHz.min}–${LIMITS.gateHz.max} Hz.`);
    }
    if (c.sweepHi <= c.sweepLo) {
      at('sweepHi', 'SWEEP_DIRECTION', 'Sweep end must be higher than the start.');
    }
    if (!Number.isInteger(c.sweepHold) || !inRange(c.sweepHold, LIMITS.sweepHold)) {
      at('sweepHold', 'SWEEP_HOLD_RANGE', `Hold must be ${LIMITS.sweepHold.min}–${LIMITS.sweepHold.max} seconds.`);
    }
  } else if (!inRange(c.gateHz, LIMITS.gateHz)) {
    at('gateHz', 'GATE_RANGE', `Frequency must be ${LIMITS.gateHz.min}–${LIMITS.gateHz.max} Hz.`);
  }

  if (inRange(c.carrierHz, LIMITS.carrierHz) && c.carrierHz > USEFUL_CARRIER_RANGE.hi) {
    at(
      'carrierHz',
      'CARRIER_INEFFECTIVE',
      `Above about ${USEFUL_CARRIER_RANGE.hi} Hz the coil can't keep up with the carrier — the extra just becomes heat.`,
      'warning',
    );
  }

  return issues;
}

/** Device storage rules for program names. */
export function validateName(name: string): Issue[] {
  const issues: Issue[] = [];
  if (name.length < LIMITS.nameLen.min || name.length > LIMITS.nameLen.max) {
    issues.push({
      severity: 'error',
      code: 'NAME_LENGTH',
      field: 'name',
      message: `Name must be ${LIMITS.nameLen.min}–${LIMITS.nameLen.max} characters.`,
    });
  }
  if (!/^[A-Za-z0-9_]*$/.test(name)) {
    issues.push({
      severity: 'error',
      code: 'NAME_CHARS',
      field: 'name',
      message: 'Letters, numbers and underscores only.',
    });
  }
  return issues;
}

export function sanitizeName(input: string): string {
  return input
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^A-Za-z0-9_]/g, '')
    .slice(0, LIMITS.nameLen.max)
    .toUpperCase();
}

/* ------------------------------------------------------------------ *
 * Avoided bands
 * ------------------------------------------------------------------ */

export function validateAgainstBands(cycles: CycleSettings[], bands: Band[]): Issue[] {
  const issues: Issue[] = [];
  if (bands.length === 0) return issues;

  cycles.forEach((c, index) => {
    if (c.sweepEnabled) {
      for (const band of bandsHittingSweep(c.sweepLo, c.sweepHi, bands)) {
        issues.push({
          severity: 'error',
          code: 'BAND_SWEEP',
          cycleIndex: index,
          field: 'sweepLo',
          message: `This sweep passes through ${band.lo}–${band.hi} Hz, which you've set to be avoided.`,
        });
      }
    } else {
      for (const band of bandsHittingPoint(c.gateHz, bands)) {
        issues.push({
          severity: 'error',
          code: 'BAND_GATE',
          cycleIndex: index,
          field: 'gateHz',
          message: `${c.gateHz} Hz is inside ${band.lo}–${band.hi} Hz, which you've set to be avoided.`,
        });
      }
    }
  });

  return issues;
}

/* ------------------------------------------------------------------ *
 * Top level
 * ------------------------------------------------------------------ */

export type ValidationResult = {
  issues: Issue[];
  errors: Issue[];
  warnings: Issue[];
  canRun: boolean;
  totalMin: number;
  treatMin: number;
};

export function validateProgram(program: Program, avoidBands: Band[] = []): ValidationResult {
  const issues: Issue[] = [];

  const n = program.cycles.length;
  if (n < LIMITS.cycles.min || n > LIMITS.cycles.max) {
    issues.push({
      severity: 'error',
      code: 'CYCLE_COUNT',
      field: 'cycles',
      message: `A program needs ${LIMITS.cycles.min}–${LIMITS.cycles.max} steps.`,
    });
  }

  issues.push(...validateName(program.name));
  program.cycles.forEach((c, i) => issues.push(...validateCycle(c, i)));
  issues.push(...validateAgainstBands(program.cycles, avoidBands));

  const errors = issues.filter((i) => i.severity === 'error');

  return {
    issues,
    errors,
    warnings: issues.filter((i) => i.severity === 'warning'),
    canRun: errors.length === 0,
    totalMin: totalSessionMinutes(program.cycles),
    treatMin: treatmentMinutes(program.cycles),
  };
}
